import { useState, type FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/d9v9shspc8jh8d0bvpsg";

type FormStatus = "idle" | "loading" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setMessageLength(0);
      return;
    }

    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, String(value)));

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();

      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || "";
      const isSpam = /spam/i.test(responseText) || /spam/i.test(serverMsg);

      if (!response.ok || !parsed || parsed.code !== "OK" || isSpam) {
        setFormError(serverMsg || "提交失败,请稍后重试。");
        setStatus("error");
        return;
      }

      setStatus("success");
      form.reset();
      setMessageLength(0);
    } catch {
      setFormError("网络异常,请检查网络后重试。");
      setStatus("error");
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
      <h3 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h3>
      <p className="mt-1.5 text-sm text-foreground-500">
        想看的漫画、阅读建议或问题反馈,都可以在这里告诉我们,客服会在 24 小时内回复。
      </p>

      {status === "success" ? (
        <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-6 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-xl text-background-50">
            <i className="ri-check-line" aria-hidden="true" />
          </span>
          <h4 className="mt-3 text-base font-bold text-foreground-950">提交成功</h4>
          <p className="mt-1 text-sm text-foreground-600">感谢你的反馈,我们会尽快与你联系!</p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            再写一条
          </button>
        </div>
      ) : (
        <form id="contactForm" data-readdy-form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex-1">
              <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                你的称呼 <span className="text-primary-600">*</span>
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                maxLength={30}
                placeholder="怎么称呼你"
                className="w-full rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                邮箱 <span className="text-primary-600">*</span>
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                maxLength={60}
                placeholder="example@email.com"
                className="w-full rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="contact-phone" className="mb-1.5 block text-sm font-medium text-foreground-800">
                联系方式
              </label>
              <input
                id="contact-phone"
                name="phone"
                type="tel"
                maxLength={20}
                placeholder="手机号 / QQ / 微信"
                className="w-full rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
              />
            </div>
          </div>

          <div>
            <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
              留言内容 <span className="text-primary-600">*</span>
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              rows={5}
              maxLength={500}
              onChange={(e) => setMessageLength(e.target.value.length)}
              placeholder="想看的漫画名称、阅读体验反馈、合作咨询等……"
              className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
            />
            <p className="mt-1 text-right text-xs text-foreground-400">{messageLength} / 500</p>
          </div>

          {/* 反垃圾蜜罐字段,对真实用户不可见 */}
          <div className="field-trap" aria-hidden="true">
            <label htmlFor="website-alt-field">Website</label>
            <input
              id="website-alt-field"
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>

          {status === "error" && (
            <p role="alert" className="rounded-md bg-red-100 px-4 py-2.5 text-sm text-red-800">
              <i className="ri-error-warning-line mr-1" aria-hidden="true" />
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {status === "loading" ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-line" aria-hidden="true" />
                提交留言
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}